import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaPlay, 
  FaArrowRight, 
  FaDesktop, 
  FaChartLine, 
  FaClock, 
  FaCheckCircle,
  FaExternalLinkAlt,
  FaHospital,
  FaSchool,
  FaUniversity,
  FaBuilding,
  FaUsers,
  FaCreditCard,
  FaTimes
} from 'react-icons/fa';

// ─── Data ──────────────────────────────────────────────────────────

const DEMOS = [
  {
    id: 'hospital',
    title: 'Hospital Management System',
    description: 'Cloud-based EMR & Billing System',
    icon: FaHospital,
    features: ['Patient Records', 'Appointment Scheduling', 'Billing & Invoicing', 'Lab Integration'],
    badge: 'Healthcare',
    category: 'Healthcare',
    demoLink: 'https://www.micropointlive.com/Medicare/signup'
  },
  {
    id: 'school',
    title: 'School Management System',
    description: 'Admissions, Exams & Finance Management',
    icon: FaSchool,
    features: ['Student Records', 'Exam Management', 'Fee Processing', 'Parent Portal'],
    badge: 'Education',
    category: 'Education',
    demoLink: 'https://schoolshine-academics-finances.lovable.app'
  },
  {
    id: 'sacco',
    title: 'SACCO Core Banking',
    description: 'Power Financials - Full GL & Member Management',
    icon: FaUniversity,
    features: ['Member Management', 'Savings Processing', 'Loan Origination', 'M-PESA Integration'],
    badge: 'Finance',
    category: 'Finance',
    demoLink: '/demo'
  },
  {
    id: 'erp',
    title: 'ERP Solutions',
    description: 'Microsoft Dynamics 365 Business Central',
    icon: FaBuilding,
    features: ['Financial Management', 'Supply Chain', 'Operations', 'Business Intelligence'],
    badge: 'Enterprise',
    category: 'Enterprise',
    demoLink: '/demo'
  },
  {
    id: 'payroll',
    title: 'HR & Payroll',
    description: 'Complete HR Management System',
    icon: FaUsers,
    features: ['Payroll Processing', 'Leave Management', 'Claims Management', 'Performance Tracking'],
    badge: 'HR',
    category: 'HR',
    demoLink: '/demo'
  },
  {
    id: 'payment',
    title: 'Payment Integration',
    description: 'MPESA, Bank APIs & Payment Switching',
    icon: FaCreditCard,
    features: ['MPESA Integration', 'Bank APIs', 'ISO8583 Switching', 'ATM Integration'],
    badge: 'Payments',
    category: 'Payments',
    demoLink: '/demo'
  },
];

const FILTER_OPTIONS = [
  { id: 'All', label: 'All Demos' },
  { id: 'Healthcare', label: 'Healthcare' },
  { id: 'Education', label: 'Education' },
  { id: 'Finance', label: 'Finance' },
  { id: 'Enterprise', label: 'Enterprise' },
  { id: 'HR', label: 'HR' },
  { id: 'Payments', label: 'Payments' },
];

const STATS = [
  { number: '6+', label: 'Interactive Demos', icon: FaDesktop },
  { number: '30+', label: 'Features to Explore', icon: FaChartLine },
  { number: '100%', label: 'Free to Try', icon: FaPlay },
  { number: '24/7', label: 'Access Available', icon: FaClock },
];

const TRUSTED_ORGANIZATIONS = ['SACCOs', 'Banks', 'Microfinance', 'NGOs', 'Government'];

const BADGE_COLORS = {
  'Healthcare': 'bg-blue-50 text-blue-700',
  'Education': 'bg-emerald-50 text-emerald-700',
  'Finance': 'bg-amber-50 text-amber-700',
  'Enterprise': 'bg-indigo-50 text-indigo-700',
  'HR': 'bg-rose-50 text-rose-700',
  'Payments': 'bg-cyan-50 text-cyan-700',
};

// ─── Subcomponents ────────────────────────────────────────────────

const DemoCard = ({ demo, index }) => {
  const Icon = demo.icon;
  const isExternal = demo.demoLink && (demo.demoLink.startsWith('http://') || demo.demoLink.startsWith('https://'));
  const badgeColor = BADGE_COLORS[demo.category] || 'bg-slate-50 text-slate-700';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -4 }}
      className="group h-full"
    >
      <div className="bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all h-full flex flex-col overflow-hidden">
        
        {/* Card Header - Clean, minimal */}
        <div className="p-5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <span className={`inline-block text-[10px] px-2.5 py-0.5 rounded-full font-medium mb-2 ${badgeColor}`}>
                {demo.badge}
              </span>
              <h3 className="text-base font-bold text-slate-900 leading-tight">{demo.title}</h3>
              <p className="text-slate-500 text-xs mt-1">{demo.description}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 ml-3 group-hover:bg-blue-50 transition-colors">
              <Icon className="text-lg text-slate-600 group-hover:text-blue-600 transition-colors" />
            </div>
          </div>
        </div>
        
        {/* Card Body */}
        <div className="p-5 flex-1 flex flex-col">
          <ul className="space-y-2 mb-5 flex-1">
            {demo.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                <FaCheckCircle className="text-blue-500 flex-shrink-0 text-xs mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          
          {/* Demo Button */}
          <a
            href={demo.demoLink}
            target={isExternal ? "_blank" : "_self"}
            rel={isExternal ? "noopener noreferrer" : ""}
            className="w-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:shadow-md flex items-center justify-center gap-2"
          >
            <FaPlay className="text-xs" /> 
            {isExternal ? 'Launch Demo' : 'Access Demo'}
            {isExternal && <FaExternalLinkAlt className="text-[10px] ml-0.5" />}
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const StatsSection = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
    {STATS.map((stat, index) => {
      const Icon = stat.icon;
      return (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 + index * 0.08 }}
          whileHover={{ y: -2 }}
          className="bg-white rounded-xl p-5 text-center border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-100 transition-all">
            <Icon className="text-lg text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{stat.number}</div>
          <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
        </motion.div>
      );
    })}
  </div>
);

const CustomDemoRequest = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3 }}
    className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center bg-slate-900"
  >
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
    
    <div className="relative z-10">
      <div className="text-4xl mb-4" aria-hidden="true">🎯</div>
      <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
        Need a <span className="text-blue-400">Custom Demo</span>?
      </h2>
      <p className="text-white/60 text-sm mb-6 max-w-lg mx-auto">
        Don't see what you're looking for? Request a personalized demo tailored to your specific needs.
      </p>
      <Link to="/contact">
        <button className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-8 py-3.5 rounded-full font-semibold text-sm shadow-lg hover:shadow-blue-600/30 transition-all inline-flex items-center gap-2.5">
          Request Custom Demo <FaArrowRight className="text-xs" />
        </button>
      </Link>
    </div>
  </motion.div>
);

const TrustSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.4 }}
    className="mt-10 text-center"
  >
    <p className="text-sm text-slate-500 font-medium mb-4">Trusted by leading organizations</p>
    <div className="flex flex-wrap justify-center gap-2.5">
      {TRUSTED_ORGANIZATIONS.map((org, i) => (
        <span 
          key={i} 
          className="text-xs text-slate-600 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all cursor-default"
        >
          {org}
        </span>
      ))}
    </div>
  </motion.div>
);

const EmptyState = ({ activeFilter }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="text-center py-16 bg-white rounded-xl border border-slate-200"
  >
    <div className="text-6xl mb-4" aria-hidden="true">🔍</div>
    <h3 className="text-xl font-bold text-slate-900 mb-2">No Demos Found</h3>
    <p className="text-slate-600 text-sm">
      No demos available in <span className="font-semibold">{activeFilter}</span>. Try selecting a different category.
    </p>
  </motion.div>
);

// ─── Main Component ──────────────────────────────────────────────

export default function DemoCenter() {
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredDemos = useMemo(() => {
    return activeFilter === 'All' 
      ? DEMOS 
      : DEMOS.filter(demo => demo.category === activeFilter);
  }, [activeFilter]);

  const handleFilterChange = useCallback((filterId) => {
    setActiveFilter(filterId);
  }, []);

  const handleClearFilter = useCallback(() => {
    setActiveFilter('All');
  }, []);

  return (
    <section className="py-12 md:py-16 bg-slate-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3 border border-blue-100">
            Live Demos
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            Try Our <span className="text-blue-600">Live Demos</span>
          </h1>
          <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-sm">
            Experience our solutions firsthand with no commitment required. 
            Click any demo to get started.
          </p>
        </motion.div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex flex-wrap gap-1.5">
            {FILTER_OPTIONS.map((filter) => {
              const isActive = activeFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                      : 'bg-white text-slate-600 hover:text-blue-600 hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">
              {filteredDemos.length} {filteredDemos.length === 1 ? 'demo' : 'demos'} available
            </span>
            {activeFilter !== 'All' && (
              <button
                onClick={handleClearFilter}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
              >
                <FaTimes className="text-xs" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Demo Cards Grid */}
        <AnimatePresence mode="wait">
          {filteredDemos.length === 0 ? (
            <EmptyState activeFilter={activeFilter} />
          ) : (
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            >
              {filteredDemos.map((demo, index) => (
                <DemoCard key={demo.id} demo={demo} index={index} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats */}
        <StatsSection />

        {/* Custom Demo Request */}
        <CustomDemoRequest />

        {/* Trust Section */}
        <TrustSection />

      </div>
    </section>
  );
}